import type { Week, WeekUpdate, WeekTasks } from '../types';

// Configure API base URL based on environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 
  (import.meta.env.DEV ? 'http://localhost:8001' : 'https://api.clazzo.uk');

class WeeksService {
  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  async getCurrentWeek(teamId?: string): Promise<Week> {
    const params = teamId ? `?team_id=${teamId}` : '';
    const response = await fetch(`${API_BASE_URL}/weeks/current${params}`, {
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch current week');
    }
    
    return response.json();
  }

  async getWeeks(limit = 10, offset = 0, teamId?: string): Promise<Week[]> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
      ...(teamId && { team_id: teamId }),
    });
    
    const response = await fetch(`${API_BASE_URL}/weeks?${params}`, {
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch weeks');
    }
    
    return response.json();
  }

  async getWeek(weekId: string): Promise<Week> {
    const response = await fetch(`${API_BASE_URL}/weeks/${weekId}`, {
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch week');
    }
    
    return response.json();
  }

  async updateWeek(weekId: string, data: WeekUpdate): Promise<Week> {
    const response = await fetch(`${API_BASE_URL}/weeks/${weekId}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error('Failed to update week');
    }
    
    return response.json();
  }

  async getWeekTasks(weekId: string): Promise<WeekTasks> {
    const response = await fetch(`${API_BASE_URL}/weeks/${weekId}/tasks`, {
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch week tasks');
    }
    
    return response.json();
  }

  async completeWeeklyReview(weekId: string): Promise<Week> {
    const response = await fetch(`${API_BASE_URL}/weeks/${weekId}/complete-review`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to complete weekly review');
    }
    
    return response.json();
  }
}

export const weeksService = new WeeksService();